import React from "react";
import { ArrowLeft, ArrowRight, Menu } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { hasAppMenuButton, openAppMenu } from "../../utils/desktopShell";
import SidebarToggleIcon from "../SidebarToggleIcon";

type WindowControlsProps = {
  collapsed: boolean;
  onToggleCollapse: () => void;
};

// instant-tooltip: icon-only buttons need a label on hover, and the native
// title attribute's delay is too long for a toolbar.
const button =
  "instant-tooltip instant-tooltip-below relative flex h-7 w-7 items-center justify-center rounded-md text-text-dim transition hover:bg-muted-surface hover:text-text";

/**
 * Sidebar toggle and history navigation, at the left end of the title bar.
 *
 * Rendered inside whichever element owns that band — the sidebar's top strip
 * when expanded, the toolbar when collapsed — rather than floating over it:
 * `app-no-drag` only reliably cuts a hole in a drag region from inside, and as
 * a sibling the drag rect wins and the buttons stop responding.
 */
const WindowControls = ({ collapsed, onToggleCollapse }: WindowControlsProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toggleLabel = collapsed ? t("expand_sidebar") : t("collapse_sidebar");

  return (
    <div className="app-no-drag flex shrink-0 items-center gap-0.5">
      {/* A hamburger rather than the app mark: on Windows the icon in that
          corner has meant the window's system menu since Win32, so the mark
          would promise Move/Size/Close. */}
      {hasAppMenuButton && (
        <button
          type="button"
          onClick={event => openAppMenu(event.currentTarget)}
          className={`${button} mr-1.5`}
          data-tooltip={t("app_menu")}
          aria-label={t("app_menu")}
        >
          <Menu size={17} />
        </button>
      )}

      <button
        type="button"
        onClick={onToggleCollapse}
        className={button}
        data-tooltip={toggleLabel}
        aria-label={toggleLabel}
        aria-expanded={!collapsed}
      >
        <SidebarToggleIcon collapsed={collapsed} side="left" />
      </button>

      <div className="ml-3 flex items-center gap-1">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className={button}
          data-tooltip={t("go_back")}
          aria-label={t("go_back")}
        >
          <ArrowLeft size={20} strokeWidth={1.75} />
        </button>
        <button
          type="button"
          onClick={() => navigate(1)}
          className={button}
          data-tooltip={t("go_forward")}
          aria-label={t("go_forward")}
        >
          <ArrowRight size={20} strokeWidth={1.75} />
        </button>
      </div>
    </div>
  );
};

export default WindowControls;
